import { Route } from '@/Enums/Routes/Route.enum';
import FiltersBadge from '@/components/Filter/Badge';
import LoadingBackdrop from '@/components/Grid/LoadingBackdrop';
import FilterIcon from '@/components/Icons/FilterIcon';
import SearchInput from '@/components/SearchInput';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setFilters } from '@/store/slices/forecasterSlice';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import {
  GridApiPro,
  GridCsvGetRowsToExportParams,
  GridRowId,
  GridToolbarContainer,
  gridFilteredSortedRowIdsSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, MouseEvent, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FiltersPopover from '../FiltersPopover';

const getSelectedRowsToExport = ({ apiRef }: GridCsvGetRowsToExportParams<GridApiPro>): GridRowId[] => {
  return gridFilteredSortedRowIdsSelector(apiRef);
};

const ToolBar = () => {
  const apiRef = useGridApiContext();

  const dispatch = useAppDispatch();

  const { forecasts, forecastsRequestId, filters } = useAppSelector(state => state.forecaster);
  const [search, setSearch] = useState<string>(filters?.search || '');

  const updateSearchFilter = useCallback(
    debounce((newSearch: string) => {
      dispatch(setFilters({ search: newSearch }));
    }, 500),
    [dispatch],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchValue = event.target?.value;
      setSearch(searchValue);
      updateSearchFilter(searchValue);
    },
    [updateSearchFilter],
  );

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenFiltersModal = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => setFilterAnchorEl(currentTarget),
    [setFilterAnchorEl],
  );

  const handleCloseFiltersModal = useCallback(
    (_: SyntheticEvent, reason: string) => {
      if (reason) return;
      setFilterAnchorEl(null);
    },
    [setFilterAnchorEl],
  );

  const filtersOpen = Boolean(filterAnchorEl);
  const filtersId = filtersOpen ? 'filters-popover' : undefined;

  const filtersBadgeCount = useMemo(() => {
    if (!filters) return 0;
    return (filters.channel?.length || 0) + (filters.packages?.length || 0);
  }, [filters]);

  const onGenerateReportClick = useCallback(() => {
    apiRef.current.setColumnVisibility('geo_fence', true);
    apiRef.current.setColumnVisibility('geo_exclusion', true);

    apiRef.current.exportDataAsCsv({
      //@ts-expect-error pro and community version mismatch
      getRowsToExport: getSelectedRowsToExport,
      fileName: `Forecasts_${Date.now()}`,
    });

    apiRef.current.setColumnVisibility('geo_fence', false);
    apiRef.current.setColumnVisibility('geo_exclusion', false);
  }, [apiRef]);

  return (
    <>
      <GridToolbarContainer
        sx={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div className='flex justify-between items-center'>
          <div className='mb-[10px]'>
            <h2 className='mb-[5px]'>
              <span className='text-[#171725] text-[24px] font-semibold'>Forecaster</span>
              <span className='text-[#C1BFC2] text-[20px] pl-[2px]'>&nbsp;({forecasts.length})</span>
            </h2>
          </div>
        </div>
        <div className='flex justify-end gap-2'>
          <div className='table-controls flex gap-[10px]'>
            <SearchInput required placeholder='Search..' value={search} onChange={onSearchChange} />
            <Button
              aria-describedby={filtersId}
              variant='outlined'
              onClick={handleOpenFiltersModal}
              sx={{
                display: 'flex',
                gap: '4px',
              }}
            >
              <FilterIcon className='w-[16px]' />
              <span>Filters</span>
              <FiltersBadge count={filtersBadgeCount} />
            </Button>
            <Button variant='outlined' onClick={onGenerateReportClick}>
              <FileDownloadOutlined sx={{ width: 20, height: 20, marginRight: '4px' }} />
              <span>Export</span>
            </Button>
            <FiltersPopover
              id={filtersId}
              open={filtersOpen}
              anchorEl={filterAnchorEl}
              handleClose={handleCloseFiltersModal}
              filtersBadgeCount={filtersBadgeCount}
            />
          </div>
          <Link to={Route.FORECASTER}>
            <Button sx={{ height: 40, width: 135, borderRadius: 6 }} color='primary' variant='contained'>
              + Create New
            </Button>
          </Link>
        </div>
        {!!forecastsRequestId && <LoadingBackdrop />}
      </GridToolbarContainer>
    </>
  );
};

export default ToolBar;
